import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import {
    ChatbotAdminService,
    KnowledgeHubAdminService,
    TagsAdminService,
    tags_Tag,
} from "src/api"
import { CustomDataHelper } from "src/api/_custom/services/DataHelper"
import { Channel } from "src/channel"
import { loads } from "src/channel/utils"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { createLoadingKeys } from "src/lib/loading"
import { Pagination } from "src/lib/pagination"
import { reportError } from "src/lib/report"
import {
    IAdvanceQueryModel,
    IGenericSingleSelectOptions,
} from "src/types/data-grid-pro"
import { IKnowledgeItem } from "src/views/knowledge-base/pages/list/types"

export class KnowledgeBaseListStore {
    //#region initialization
    static Context = React.createContext<KnowledgeBaseListStore | null>(null)
    static LoadingKeys = createLoadingKeys("loading")
    knowledgeItems = new Pagination(
        async (params) => {
            const response =
                await KnowledgeHubAdminService.postV1AdminKnowledgebaseKnowledgeItemsList(
                    {
                        request: {
                            filter: params.advanceQuery,
                            page_number: params.page,
                            page_size: params.pageSize,
                            ...CustomDataHelper.PrepareVisibilityData(this),
                        },
                    },
                )

            const items: IKnowledgeItem[] = (
                response.knowledge_items ?? []
            ).map((item) => ({
                id: item.id ?? -1,
                title: item.title ?? "",
                access_group_id: item.access_group_id ?? -1,
                description: item.description ?? "",
                segments: item.segments ?? [],
                segment_names: item.segment_names ?? [],
                tags: item.tags ?? [],
                agents: item.agents ?? [],
                agent_types: item.agent_types ?? [],
                dimensions: item.dimensions ?? [],
                content: item.content ?? "",
                knowledge_item_type: item.knowledge_item_type ?? undefined,
                access_type: item.access_type ?? "NONE",
                source_item_id: item.source_item_id,
                source_item_type: item.source_item_type,
                is_available_for_chatbot:
                    item.is_available_for_chatbot ?? false,
                search_instructions: item.search_instructions ?? "",
            }))

            return {
                items,
                count: response.total_count ?? 0,
            }
        },
        {
            loadingKey: KnowledgeBaseListStore.LoadingKeys.loading,
        },
    )
    private repositoryUpdatesListenerDisposer?: () => void

    constructor() {
        makeAutoObservable(this)
    }

    _segments: number[] = []

    //#region getters
    get segments() {
        return this._segments
    }

    _agentAccessTypes: IGenericSingleSelectOptions[] = []
    _agentConfigs: Array<{
        value: number
        label: string
        agentType: string
        isInstanceRequired: boolean
    }> = []
    _tags: tags_Tag[] = []
    //#endregion

    get agentAccessTypes() {
        return this._agentAccessTypes
    }

    get agentConfigs() {
        return this._agentConfigs
    }

    get tags() {
        return this._tags
    }

    _accessGroupId: number = DEFAULT_ACCESS_GROUP.id

    private get accessGroupId() {
        return this._accessGroupId
    }
    //#endregion

    setAgentAccessTypes = (types: IGenericSingleSelectOptions[]) => {
        this._agentAccessTypes = types
    }

    setAgentConfigs = (
        configs: Array<{
            value: number
            label: string
            agentType: string
            isInstanceRequired: boolean
        }>,
    ) => {
        this._agentConfigs = configs
    }

    setTags = (tags: tags_Tag[]) => {
        this._tags = tags
    }

    //#region store operations
    async init(accessGroupId: number, advanceQuery?: IAdvanceQueryModel) {
        this.listenToRepositoryUpdates()

        this.setAccessGroup(accessGroupId)
        try {
            await this.knowledgeItems.loadInitialPage(advanceQuery)
            await this.fetchAgentTypes()
            await this.fetchAgentConfigs()
            await this.fetchTags()
        } catch (error) {
            reportError(t`knowledge-base.init-fail`, error)
        }
    }

    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }
    //#endregion

    @loads(() => KnowledgeBaseListStore.LoadingKeys.loading)
    async fetchAgentTypes() {
        try {
            const configDefinition =
                await ChatbotAdminService.getV1AdminChatbotAgentConfigConfigDefinition(
                    {
                        space: "knowledgebase",
                    },
                )

            if (
                configDefinition.definition != null &&
                configDefinition.definition.length > 0
            ) {
                const types = configDefinition.definition.map((field) => ({
                    value: field.config_definition_id ?? 0,
                    label: field.name ?? "",
                }))

                this.setAgentAccessTypes(types)
            }
        } catch (error) {
            reportError(t`knowledge-item-modal.load-agent-types-fail`, error)
        }
    }

    @loads(() => KnowledgeBaseListStore.LoadingKeys.loading)
    async fetchAgentConfigs() {
        try {
            const visibilityData = CustomDataHelper.PrepareVisibilityData(this)

            const agentConfigs =
                await ChatbotAdminService.postV1AdminChatbotAgentConfigList({
                    request: {
                        filter: {
                            items: [],
                        },
                        page_number: 1,
                        page_size: 1000, // Get all configs
                        access_group_id: visibilityData.access_group_id,
                        segment_ids: visibilityData.segment_ids,
                    },
                })

            if (
                agentConfigs.configs != null &&
                agentConfigs.configs.length > 0
            ) {
                // Process configs to extract agent types and metadata
                const configs = agentConfigs.configs
                    .map((config) => {
                        const configKeys = Object.keys(config.config ?? {})
                        const agentType = configKeys.find(
                            (key) =>
                                key === "qa_agent" || key === "public_agent",
                        )

                        if (agentType == null || agentType === "") {
                            return null // Skip configs that don't have our supported agent types
                        }

                        return {
                            value: config.feature_config_id ?? 0,
                            label: config.name ?? "",
                            agentType: agentType,
                            isInstanceRequired:
                                config.meta_data?.agent_instance_required ??
                                false,
                        }
                    })
                    .filter(Boolean) as Array<{
                    value: number
                    label: string
                    agentType: string
                    isInstanceRequired: boolean
                }>

                this.setAgentConfigs(configs)
            }
        } catch (error) {
            reportError(t`knowledge-item-modal.load-agent-configs-fail`, error)
        }
    }

    @loads(() => KnowledgeBaseListStore.LoadingKeys.loading)
    async fetchTags() {
        try {
            const response = await TagsAdminService.postV1AdminTagList({
                request: { page_size: 10000 },
            })

            if (response.tags != null && response.tags.length > 0) {
                this.setTags(response.tags)
            }
        } catch (error) {
            reportError(t`knowledge-item-modal.load-tags-fail`, error)
        }
    }

    async loadSegments(segments: number[], advanceQuery?: IAdvanceQueryModel) {
        this.setSegments(segments)
        await this.knowledgeItems.loadInitialPage(advanceQuery)
    }

    deleteItem = async (id: number) => {
        try {
            await KnowledgeHubAdminService.deleteV1AdminKnowledgebaseKnowledgeItems(
                {
                    knowledgeItemId: id,
                },
            )
        } catch (error) {
            reportError(t`template-features.delete-item-fail`, error)
        } finally {
            await this.knowledgeItems.reload()
        }
    }

    async query(advanceQuery: IAdvanceQueryModel) {
        await this.knowledgeItems.loadAdvanceQuery(advanceQuery)
    }

    //#region setters
    private setSegments(segments: number[]) {
        this._segments = segments
    }

    private setAccessGroup(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    private listenToRepositoryUpdates() {
        this.repositoryUpdatesListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    event.payload.repository === "knowledge-base"
                ) {
                    await this.knowledgeItems.reload()
                }
            },
        )
    }
    //#endregion
}
